<template>
  <SmDialog
    v-model:visibility="visibility"
    :title="i18n.t('createToken')"
    size="small">
    <div :class="['token-wrapper', { open: !tokenGenerated }]">
      <p class="mb-6">
        {{ i18n.t('newTokenDescription') }}
      </p>

      <el-form-item
        :label="i18n.t('description')"
        prop="form.fields.note"
        class="mb-6"
        label-position="top"
        :error="form.getErrorMessage('note')">
        <SmInput
          v-model="form.fields.note"
          design="form"
          @blur="form.validate(['note'])" />
      </el-form-item>

      <el-form-item
        :label="i18n.t('tokenExpiresIn')"
        prop="form.fields.note"
        class="mb-6"
        label-position="top">
        <SmSelect
          v-model:selection="expiresIn"
          class="w-full"
          :options="options"
          :label="expiresIn[0].label" />
      </el-form-item>

      <el-form-item
        :label="i18n.t('scopes')"
        prop="form.fields.scopes"
        class="mb-6"
        label-position="top">
        <SmSelect
          v-model:selection="scopes"
          class="w-full"
          :options="scopeOptions"
          :multiselect="true" />
      </el-form-item>
    </div>

    <div :class="['token-wrapper', { open: tokenGenerated }]">
      <div class="expandable">
        <div class="token-input" style="margin-bottom: 1rem">
          <SmInput
            v-model="token"
            size="small"
            outline
            label="Token"
            disabled></SmInput>
          <div>
            <SmTooltip
              v-if="isSupported && token"
              placement="right"
              :content="copied ? i18n.t('copied') : i18n.t('copy')">
              <Copy :size="18" @click="copy(token)"></Copy>
            </SmTooltip>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <SmButton
        v-if="!tokenGenerated"
        :loading="loading"
        @click="generateNewToken">
        {{ i18n.t('generateNewToken') }}
      </SmButton>
      <SmButton v-else outline @click="closeDialog">
        {{ i18n.t('close') }}
      </SmButton>
    </template>
  </SmDialog>

  <!-- settings-container defined in parent -->
  <div class="flex flex-col gap-5 p-4 pb-5 pt-5">
    <div class="settings-group">
      <div class="header-with-button">
        <!-- Header -->
        <h3>{{ i18n.t('sidebar.settings.api') }}</h3>
        <SmButton outline @click="visibility = true">
          {{ i18n.t('createToken') }}
        </SmButton>
      </div>
    </div>

    <SmTable
      style="margin-top: 1rem"
      :data="currentTokens?.company_access_tokens || []"
      key-field="_id"
      :columns="columns">
      <template #created="{ row }">
        <p>{{ displayDate(new Date(row.created), { dateStyle: 'short' }) }}</p>
      </template>

      <template #scp="{ row }">
        <p>{{ row.scp.join(', ') }}</p>
      </template>

      <template #actions="{ row }">
        <!-- Delete -->
        <div style="margin-right: auto">
          <Trash
            class="cursor-pointer"
            :size="18"
            @click="deleteToken(row._id)" />
        </div>
      </template>
    </SmTable>
  </div>
</template>

<script setup lang="ts">
  import { useClipboard } from '@vueuse/core'
  import { useI18n } from 'vue-i18n'

  import {
    AccessTokenService,
    CompanyAccessTokensResponse,
    CompanyManagementService,
    Scopes,
  } from '@/client'
  import { useSessionStore } from '@/stores/sessionStore'
  import { Column } from '../sm/SmTable.types'
  import { displayDate } from '@/utils/formatter/date'
  import { Copy, Trash } from 'lucide-vue-next'
  import useForm from '@/composables/useForm'
  import { isRequired } from '../sm/SmInput/SmInputValidator'
  import { Option } from '../sm/SmSelect.vue'
  const sessionStore = useSessionStore()
  const i18n = useI18n()

  const token = ref<string>('')

  const currentTokens = ref<CompanyAccessTokensResponse | null>(null)

  const loading = ref(false)

  const { copy, copied, isSupported } = useClipboard()

  onMounted(() => {
    getCurrentToken()
  })

  // ----------------------------------------------------------------------
  // Table
  // ----------------------------------------------------------------------

  const columns: Column<
    CompanyAccessTokensResponse['company_access_tokens'][0]
  >[] = [
    {
      key: '_id',
      label: i18n.t('id'),
      sortable: true,
      width: 0.6,
    },
    {
      key: 'created',
      label: i18n.t('created'),
      sortable: true,
      width: 0.6,
    },
    {
      key: 'name',
      label: i18n.t('creator'),
      sortable: false,
      width: 1,
    },
    {
      key: 'scp',
      label: i18n.t('scopes'),
      sortable: false,
      width: 1,
    },
    {
      key: 'notes',
      label: i18n.t('description'),
      sortable: false,
      width: 1,
    },
    {
      key: 'actions',
      label: '',
      sortable: false,
      width: '50px',
    },
  ]

  function getCurrentToken() {
    CompanyManagementService.getCompanyAccessTokensApiV1CompanyAccessTokensGet({
      isLoginAccessToken: false,
    }).then((response) => {
      currentTokens.value = response
    })
  }

  function deleteToken(id: string) {
    CompanyManagementService.deleteCompanyAccessTokenByIdApiV1CompanyAccessTokensCompanyAccessTokenIdDelete(
      {
        companyAccessTokenId: id,
      }
    ).then(() => {
      getCurrentToken()
    })
  }

  // ----------------------------------------------------------------------
  // Dialog
  // ----------------------------------------------------------------------

  const daysInSeconds = (val: number) => val * 86400 - 1
  const dayValues = [365, 30, 7, 3, 1]

  const options = ref<Option<number>[]>(
    dayValues.map((days) => ({
      label: i18n.t('days', { count: days }),
      value: daysInSeconds(days),
    }))
  )

  const scopeOptions = ref<Option<Scopes>[]>([
    { label: 'ADMIN_READ', value: Scopes.ADMIN_READ },
    { label: 'ADMIN_WRITE', value: Scopes.ADMIN_WRITE },
    { label: 'MEMBER_READ', value: Scopes.MEMBER_READ },
  ])

  const visibility = ref(false)
  const tokenGenerated = ref(false)
  const note = ref('')
  const expiresIn = ref<Option<number>[]>([options.value[0]])
  const scopes = ref<Option<Scopes>[]>([...scopeOptions.value])

  const form = useForm(
    {
      note,
      expiresIn,
      scopes,
    },
    // Form Hooks
    {},
    // Form Validators
    {
      note: [isRequired],
    },
    {
      note: i18n.t('name'),
    }
  )

  function closeDialog() {
    visibility.value = false
    tokenGenerated.value = false
  }

  watch(visibility, (val) => {
    if (!val) {
      form.reset()
      tokenGenerated.value = false
      token.value = ''
      expiresIn.value = [options.value[0]]
      scopes.value = [...scopeOptions.value]
    }
  })

  function generateNewToken() {
    if (!sessionStore.currentCompany?.company_id) return
    if (!form.validate()) return

    loading.value = true

    AccessTokenService.requestTokenTokenPost({
      requestBody: {
        company_id: sessionStore.currentCompany?.company_id,
        expires_in: expiresIn.value[0].value,
        scopes: scopes.value.map((s) => s.value),
        notes: form.fields.note,
      },
    }).then((response) => {
      token.value = response.access_token
      tokenGenerated.value = true
      loading.value = false
      getCurrentToken()
    })
  }
</script>

<style lang="scss" scoped>
  .token-wrapper {
    display: grid;
    grid-template-rows: 0fr;
    overflow: hidden;
    transition: all 0.3s ease-in-out;
  }

  .token-wrapper.open {
    grid-template-rows: 1fr;
  }

  .expandable {
    min-height: 0;
  }

  .token-input {
    display: flex;
    align-items: center;
    gap: 0.8rem;
  }

  .header-with-button {
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
  }
</style>
